import { useEffect, useMemo, useState } from 'react';
import { AGREEMENT_STATUSES, type AgreementStatus } from '@catalyst-psa/shared';
import type { CwAgreementStatusAliasDto } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { AGREEMENT_STATUS_LABELS } from '../agreements/status.js';
import { useCwAgreementStatusAliases, useSetCwAgreementStatusAliases } from './api.js';

interface AliasRow {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  cwBillingCycleName: string;
  status: AgreementStatus | '';
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `agr-status-alias-${aliasSeq}`;
}

interface AgreementStatusAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise agreement billing cycle (a dropdown) onto a PSA agreement status
 * (a dropdown). Each row links a CW billing-cycle name to one of the fixed PSA
 * agreement statuses. On the agreements sync, a CW agreement's billing cycle sets the
 * synced agreement's status through these rows; an agreement whose billing cycle has
 * no row keeps its default/previous status (the sync never fails on a missing alias).
 */
export function AgreementStatusAliasEditor({
  isOpen,
}: AgreementStatusAliasEditorProps): JSX.Element {
  const configQuery = useCwAgreementStatusAliases(isOpen);
  const saveAliases = useSetCwAgreementStatusAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwBillingCycleName: alias.cwBillingCycleName,
        status: alias.status,
      })),
    );
  }, [config]);

  const discoveredBillingCycles = config?.discoveredBillingCycles ?? [];
  const live = config?.live ?? false;

  // CW billing-cycle names already mapped, so we can hint which discovered ones remain.
  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwBillingCycleName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredBillingCycles.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (cwBillingCycleName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwBillingCycleName, status: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwAgreementStatusAliasDto[] = rows
      .filter((row): row is AliasRow & { status: AgreementStatus } =>
        Boolean(row.cwBillingCycleName.trim() && row.status),
      )
      .map((row) => ({
        cwBillingCycleName: row.cwBillingCycleName.trim(),
        status: row.status,
      }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-agreement-billing-cycles';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Agreement status from billing cycle</h3>
        <p className="mt-1 text-sm text-slate-500">
          ConnectWise agreements carry a billing cycle rather than a PSA agreement status. Map each
          ConnectWise billing cycle to a PSA agreement status below. On sync, an agreement&apos;s
          ConnectWise billing cycle sets its PSA status — an agreement whose billing cycle has no
          row here keeps its default/previous status.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading billing cycles…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load billing cycles.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise billing cycles loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise billing cycles discovered yet. Configure the connection to load
              suggestions — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredBillingCycles.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the agreement-status aliases. Make sure each row targets a valid
              agreement status.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No agreement-status aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise billing cycle</th>
                  <th className="py-2 pr-3 font-medium">PSA agreement status</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwBillingCycleName}
                        placeholder="e.g. Monthly"
                        onChange={(e) => updateRow(row.id, { cwBillingCycleName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.status}
                        onChange={(e) =>
                          updateRow(row.id, { status: e.target.value as AgreementStatus | '' })
                        }
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select status —</option>
                        {AGREEMENT_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {AGREEMENT_STATUS_LABELS[status]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save agreement statuses
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
